import units from '@/sui/data/units'
import fields from '@/sui/data/sheets/edit/fields'
import tools from '@/sui/data/sheets/edit/tools'
import microOrgs from '@/sui/data/microOrgs'
import assert from '@/sui/tools/assert'
import go from '@/gojs/go'

function unitTypeEquals (v) {
  assert.notNull(v)
  const result = (d, s) => {
    return s[fields.unitType] === v
  }

  Object.defineProperty(result, 'name', { value: `(unitTypeEquals ${v})` })
  return result
}

function unitTypeMatch (v) {
  assert.notNull(v)
  const result = (d, s) => {
    const u = s[fields.unitType]
    return u != null && u.match(v) != null
  }

  Object.defineProperty(result, 'name', { value: `(unitTypeMatch ${v})` })
  return result
}

function mixEquals (v) {
  assert.notNull(v)
  const result = (d, s) => {
    return s[fields.mixManagment] === v
  }

  Object.defineProperty(result, 'name', { value: `(mixEquals ${v})` })
  return result
}

const isMix = unitTypeEquals('MIX REA')
const isNeo = unitTypeMatch(/^NEO/)
const isDia = unitTypeMatch(/^AD DIA/)
const isNeoOrAdDia = tools.oneTrue(isNeo, isDia)
const isNeoOrDia = unitTypeMatch(/^NEO|DIA/)
const isPed = unitTypeMatch(/^PED/)
const isAd = unitTypeMatch(/^AD/)
const isAdOrPed = unitTypeMatch(/^(AD|PED)/)
const isNeoMix = mixEquals(2)
const isPedMix = mixEquals(1)
const isNeoOrNeoMix = tools.oneTrue(isNeo, isNeoMix)
const isPedOrPedMix = tools.oneTrue(isPed, isPedMix)
const isAdPedOrPedMix = tools.oneTrue(isAdOrPed, isPedMix)
const isPedOrMix = tools.oneTrue(isPed, isMix)
const isDeathWithFuture = tools.equals(fields.future, 2)
const isDead = tools.equals(fields.dead, 1)
const exited = tools.equals2(fields.dead, 2, 9)
const isRea = unitTypeMatch(/REA/)
const hasBmrBhre = tools.equals(fields.portageBmrBhre, 1)

const select = {
  isMix,
  isNeo,
  isNeoOrAdDia,
  isNeoOrDia,
  isPed,
  isAd,
  isNeoMix,
  isPedMix,
  isNeoOrNeoMix,
  isPedOrPedMix,
  isAdPedOrPedMix,
  isPedOrMix,
  isDeathWithFuture,
  isDead,
  exited,
  isRea,
  hasBmrBhre
}

const pimII = tools.cfg.numeric(
  fields.pimII, 'Score de sévérité (PIM II)', 3, {
    tt: 'Compris entre 0 et 100, 999 si non connu.',
    sel: select.isPedOrPedMix
  }
)

const cribII = tools.cfg.numeric(
  fields.cribII, 'Score de sévérité (CRIB II)', 3, {
    tt: 'Compris entre 0 et 27, 999 si non connu.',
    sel: select.isNeo
  }
)

const igsII = tools.cfg.numeric(
  fields.igsII, 'Score de sévérité (IGS II)', 3, {
    tt: 'Compris entre 0 et 163, 999 si non connu.',
    sel: select.isAd
  }
)

const death = tools.cfg.date(
  fields.death, 'de décès', {
    tt: 'jj/mm/aaaa, postérieure à toutes les autres dates.'
  }
)

const sheetNumber = tools.cfg.numeric(fields.sheetNumber, 'Numéro de fiche', 4)

const titles = {
  patientOrigin: 'Provenance'
}

const ufs = (function () {
  function build (field, list, prefix) {
    const addPrefix = (d, f) => d == null ? f : `${d}${f}`
    const uf = addPrefix(prefix, fields.uf)
    const unitType = addPrefix(prefix, fields.unitType)
    return tools.cfg.list(
      uf, 'Identifiant du service (UF)', [], {
        tt: `Rechercher par UF, code service ou par description parmi les
UFs préalablement inscrites.`,
        ph: `Taper l'UF, le code service ou la description...`,

        ini: function (vm) {
          let result = []
          let resultTypes = {}
          const s = vm.surveillance

          const types = units[s.year][list]
          s.modules[field].ufs.forEach(
            unit => {
              const type = types.find(e => e.index === unit.ufType)
              if (type != null) {
                result.push(
                  {
                    code: unit.name,
                    value: `${unit.name} - ${type.value}`
                  }
                )
                resultTypes[unit.name] = type.code
              }
            }
          )
          this.config.values = result
          this.config.types = resultTypes
        },

        cm: function (vm, e, target) {
          vm.$set(target, uf, e)
          vm.$set(target, unitType, this.config.types[e])
        }
      }
    )
  }

  return {
    reaBactADI: build('reaBactADI', 'infADI'),
    reaInfADI: build('reaInfADI', 'infADI'),
    neoInfADI: build('neoInfADI', 'infADI'),
    diaInfADI: build('diaInfADI', 'infADI'),
    expADI: build('expADI', 'list'),
    expADISep: build('expADI', 'neoAndMix', 'sep')
  }
})()

const unitType = (function () {
  function build (ro, y) {
    return tools.cfg.list(
      fields.unitType, 'Code du service', units[y].list, {
        ph: 'Taper quelques lettres du code service ou de la spécialité...',
        tt: `Taper quelques lettres pour rechercher le code service ou
la spécialité : ${tools.key('ped r')} pour la réanimation pédiatrique,
${tools.key('ad med d')} pour la dermatologie, etc.`,
        ro: ro,
        dv: !ro
      }
    )
  }

  return {
    v1: {
      ro: build(true, '2019'),
      rw: build(false, '2019')
    },

    v2: {
      ro: build(true, '2024'),
      rw: build(false, '2024')
    }
  }
})()

const portageBmrBhre = tools.cfg.list(
  fields.portageBmrBhre, 'Portage BMR / BHRE', go.fields.yesNoUnknown
)

const bmrBhreMicroOrg = (function () {
  function build (c, v) {
    return tools.cfg.list(
      c, 'Micro-Organisme', microOrgs.bmrBhre[v], {
        h: 'Taper le code ou le nom...'
      }
    )
  }

  return {
    v1: tools.select(build(fields.bmrBhreMicroOrg, 'v1'), select.hasBmrBhre),
    v2: tools.select(
      {
        field: fields.bmrBhreMicroOrgs,
        type: 'array',
        config: {
          title: 'MICRO-ORGANISME BMR / BHRE N°',
          fields: [
            tools.opts(
              build(fields.microOrgCode, 'v2'),
              {
                pop: function (d) {
                  const mo = d[fields.microOrgCode]
                  return mo == null || mo === -1
                }
              }
            )
          ]
        }
      },
      select.hasBmrBhre
    )
  }
})()

const birthSite = tools.cfg.list(
  fields.birthSite, 'Lieu de naissance', go.fields.neoBirthSite
)

const neoVentilation = tools.cfg.list(
  fields.ventilation, 'Ventilation', go.fields.neoVentilation
)

const leucomalacy = tools.cfg.list(
  fields.leucomalacy, 'Leucomalacie', go.fields.yesNoUnknown
)

const uf = tools.cfg.text(
  fields.uf, 'Identifiant du service (UF)', {
    ph: 'UF, site...',
    tt: `Remplir ce champ si vous souhaitez distinguer vos résultats
par entités : nom de l'UFs, du site, ... Appuyer sur ${tools.key('Entrée ⏎')}
pour ignorer.`,
    op: true
  }
)

const immuno = tools.cfg.list(
  fields.immuno, 'Immunodépression', go.fields.immuno
)

export default {
  uf: uf,
  select: select,
  pimII: {
    std: pimII,
    mix: tools.select(pimII, select.isPedOrPedMix)
  },
  cribII: {
    std: cribII,
    mix: tools.select(cribII, select.isNeoOrNeoMix)
  },
  igsII,
  ufs,
  unitType,
  portageBmrBhre,
  bmrBhreMicroOrg,
  birthSite,
  neoVentilation,
  leucomalacy,

  sheetNumber: {
    v1: tools.opts(
      sheetNumber, { tt: `À votre convenance, compris entre 0 et 9999.` }
    ),

    v2: tools.opts(sheetNumber, {
      tt: `Le numéro de fiche est généré par le serveur lorsque la fiche
est enregistrée la première fois.`,
      ro: true,
      op: true
    })
  },

  birthdate: tools.cfg.date(fields.birthdate, 'de naissance'),

  sex: tools.cfg.list(
    fields.sex, 'Sexe', go.fields.sex
  ),

  entrydate: tools.cfg.date(fields.entrydate, `d'entrée`),

  cancer: tools.cfg.list(
    fields.cancer, 'Cancer évolutif', go.fields.cancer
  ),

  immuno,

  mixManagment: tools.cfg.list(
    fields.mixManagment, 'Prise en charge du patient', go.fields.mixManagment, {
      tt: `Permet d'accéder aux champs spécifiques aux enfants ou aux
nouveau-nés : poids de naissance, âge gestationnel, etc.`,
      sel: select.isMix
    }
  ),

  patientOrigin: {
    v1: tools.cfg.list(
      fields.patientOrigin, titles.patientOrigin, go.fields2019.origin, {
        ms: true,
        tt: `Taper la ou les premières lettres du code :
${tools.key('d')} pour DOM, ${tools.key('r')} pour REA, etc.`,
        ph: '«d» pour DOM, «r» pour REA...'
      }
    ),

    v2: tools.cfg.list(
      fields.patientOrigin, titles.patientOrigin, go.fields2020.origin
    ),

    v3: tools.cfg.list(
      fields.patientOrigin, titles.patientOrigin, go.fields.origin
    )
  },

  neoOrigin: tools.cfg.list(
    fields.neoOrigin, titles.patientOrigin, go.fields.neoOrigin
  ),

  birthWeight: tools.cfg.numeric(
    fields.birthWeight, 'Poids de naissance', 4, {
      un: 'grammes', tt: 'Entrer le poids de naissance en grammes.'
    }
  ),

  gestationalAge: tools.cfg.numeric(
    fields.gestationalAge, 'Âge gestationnel', 2, {
      un: 'SA', tt: `Entrer l'âge gestationnel en semaines d'aménorrhée.`
    }
  ),

  future: tools.cfg.list(
    fields.future, 'Devenir du patient', go.fields.future
  ),

  deathDateWithFuture: tools.select(death, select.isDeathWithFuture),

  dead: tools.cfg.list(
    fields.dead, 'Décès', go.fields.yesNoUnknown
  ),

  deathDate: tools.select(death, select.isDead),

  exitDate: tools.cfg.date(
    fields.exit, 'de sortie', {
      tt: `jj/mm/aaaa, après la date d'entrée.`,
      sel: select.exited
    }
  ),

  trauma: tools.cfg.list(
    fields.trauma, 'Patient traumatisé', go.fields.yesNoUnknown
  ),

  diagnosis: tools.cfg.list(
    fields.diagnosis, 'Catégorie diagnostique', go.fields.diagnosisCategory
  ),

  antibio: tools.cfg.list(
    fields.antibio, `Antibiotique à l'admission`, go.fields.yesNoUnknown
  ),

  otherSeverity: tools.cfg.text(
    fields.severityType, 'Autre score de sévérité (Nom)', {
      ph: 'Nom...',
      op: true,
      tt: `Taper le nom du score de sévérité alternatif utilisé. Appuyer
sur ${tools.key('Entrée ⏎')} pour ignorer.`
    }
  ),

  otherSeverityScore: tools.cfg.numeric(
    fields.severityScore, 'Autre score de sévérité (Score)', 5, {
      ph: 'Score...',
      op: true,
      tt: `Entrer le score de sévérité. Appuyer sur ${tools.key('Entrée ⏎')}
pour ignorer.`
    }
  ),

  bmi: tools.cfg.numeric(
    fields.bmi, `IMC`, 3, {
      tt: 'IMC = Poids (kg) / Taille² (m²), 999 si non connu.',
      ph: 'IMC...'
    }
  )
}
